import { API_METHODS } from '@compass/client/constants/api-methods';
import type {
  Evaluation,
  EvaluationReviewInput,
  UpdateEvaluationRequest,
} from '@compass/client/types/evaluation';
import { queryKeys } from '@services/query.config';
import { useIsFetching } from '@tanstack/react-query';
import { Check, CircleAlert, Database, Loader2, MoreHorizontal, X } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Alert, AlertDescription as UiAlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Textarea } from '@/components/ui/textarea';
import { useAPIPermission } from '@/hooks/usePermissions';
import { cn } from '@/utils';

import { useUpdateEvaluation } from '../hooks/useUpdateEvaluation';
import {
  countUnreviewedFlags,
  getVisibleQcReviewAlerts,
  type QcReviewAlert,
} from '../qcDetail.utils';

// ─── Confirmed badge ───────────────────────────────────────────────────────────

const ConfirmedBadge: React.FC = () => (
  <span className="inline-flex items-center gap-1.5 rounded-md border border-transparent bg-positive-muted px-2.5 py-1 text-xs font-semibold text-positive-foreground">
    <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
    Confirmed
  </span>
);

// ─── Single alert banner ───────────────────────────────────────────────────────

interface AlertBannerProps {
  alert: QcReviewAlert;
}

const ExpandableAlertDescription: React.FC<{ description: string }> = ({ description }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const descriptionRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    setIsExpanded(false);
  }, [description]);

  useEffect(() => {
    const element = descriptionRef.current;
    if (!element) {
      return;
    }

    const updateTruncation = () => {
      if (isExpanded) {
        return;
      }

      setIsTruncated(element.scrollHeight > element.clientHeight + 1);
    };

    updateTruncation();

    if (typeof ResizeObserver === 'undefined' || isExpanded) {
      return;
    }

    const resizeObserver = new ResizeObserver(updateTruncation);
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [description, isExpanded]);

  return (
    <div className="mt-1">
      <p
        ref={descriptionRef}
        className={cn(
          'text-sm leading-relaxed text-critical-foreground/90',
          !isExpanded && 'line-clamp-2',
        )}
      >
        {description}
      </p>
      {isTruncated && (
        <button
          type="button"
          className="mt-1 text-xs font-semibold text-critical-foreground transition-colors hover:text-critical-foreground/80"
          onClick={() => setIsExpanded(prev => !prev)}
          aria-expanded={isExpanded}
        >
          {isExpanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
};

const AlertBanner: React.FC<AlertBannerProps> = ({ alert }) => {
  const isReviewed = alert.review?.accepted !== undefined;
  const isConfirmed = alert.review?.accepted === true;

  return (
    <div role="alert" className="rounded-lg border border-critical-border bg-critical/60 px-4 py-3">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 pt-0.5">
          <CircleAlert className="mt-px h-4 w-4 shrink-0 text-critical-foreground" />
          <div className="min-w-0">
            <p className="text-sm font-semibold leading-5 text-critical-foreground">
              {alert.title}
            </p>
            <ExpandableAlertDescription description={alert.description} />
          </div>
        </div>

        <div className="shrink-0 pt-0.5">{isReviewed && isConfirmed && <ConfirmedBadge />}</div>
      </div>
    </div>
  );
};

// ─── Evidence box ─────────────────────────────────────────────────────────────

const CurrentEvidenceBox: React.FC<{ description: string }> = ({ description }) => (
  <DialogDescription asChild>
    <Alert
      className="mt-3 border-critical-border bg-critical/60 text-critical-foreground"
      density="default"
    >
      <CircleAlert />
      <UiAlertDescription className="block text-critical-foreground/90">
        <span className="font-semibold">Current Evidence:</span> {description}
      </UiAlertDescription>
    </Alert>
  </DialogDescription>
);

// ─── Review decision modal ────────────────────────────────────────────────────

interface PendingReviewDecision {
  alert: QcReviewAlert;
}

interface ReviewDecisionDialogProps {
  decision: PendingReviewDecision | null;
  isLoading: boolean;
  onSubmit: (reason: string) => void;
  onCancel: () => void;
}

const ReviewDecisionDialog: React.FC<ReviewDecisionDialogProps> = ({
  decision,
  isLoading,
  onSubmit,
  onCancel,
}) => {
  const [reason, setReason] = useState('');

  useEffect(() => {
    setReason('');
  }, [decision]);

  const canSubmit = reason.trim().length > 0;
  const title =
    decision?.alert.type === 'escalation'
      ? 'Dismiss Escalation'
      : decision
        ? `Dismiss ${decision.alert.title}`
        : 'Dismiss flag';

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit || isLoading) return;
    onSubmit(reason.trim());
  };

  return (
    <Dialog open={decision !== null} onOpenChange={open => !open && !isLoading && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {decision?.alert.description && (
            <CurrentEvidenceBox description={decision.alert.description} />
          )}
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <label className="block space-y-2">
            <span className="text-sm font-medium text-foreground">Reason for Dismissal</span>
            <Textarea
              placeholder="Add a reason for dismissal"
              value={reason}
              onChange={event => setReason(event.target.value)}
              disabled={isLoading}
              rows={3}
              aria-label="Review reason"
              className="border-border bg-background text-foreground shadow-none placeholder:text-muted-foreground focus-visible:ring-ring"
            />
          </label>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit || isLoading}>
              {isLoading && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              Submit
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ─── Accept confirmation modal ────────────────────────────────────────────────

interface AcceptConfirmationDialogProps {
  alert: QcReviewAlert | null;
  isLoading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const AcceptConfirmationDialog: React.FC<AcceptConfirmationDialogProps> = ({
  alert,
  isLoading,
  onConfirm,
  onCancel,
}) => (
  <Dialog open={alert !== null} onOpenChange={open => !open && !isLoading && onCancel()}>
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>{alert ? `Confirm ${alert.title}?` : 'Confirm flag?'}</DialogTitle>
        {alert && (
          <DialogDescription>
            Are you sure you want to confirm this {alert.title}? This action is{' '}
            <span className="font-semibold text-foreground">permanent and cannot be undone.</span>
          </DialogDescription>
        )}
      </DialogHeader>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button type="button" onClick={onConfirm} disabled={isLoading}>
          {isLoading && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
          Yes, confirm
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// ─── Payload builders ──────────────────────────────────────────────────────────

const buildReviewPayload = (
  alert: QcReviewAlert,
  orgId: string | undefined,
  evalId: string | undefined,
  isLastUnreviewed: boolean,
  reason: string | undefined,
  newValue: boolean,
): UpdateEvaluationRequest => {
  const accepted = newValue === alert.currentValue;
  const reviewInput: EvaluationReviewInput = {
    accepted,
    previous_value: alert.currentValue,
  };
  const trimmedReason = reason?.trim();

  if (trimmedReason) {
    reviewInput.reason = trimmedReason;
  }

  const payload: UpdateEvaluationRequest = {
    organization_id: orgId,
    evaluation_id: evalId,
    reviews: { [alert.flagKey]: reviewInput },
    ...(isLastUnreviewed ? { needs_review: false } : {}),
  };

  if (newValue !== alert.currentValue) {
    payload.flags = { [alert.flagKey]: newValue };
  }

  return payload;
};

// ─── Review actions menu ──────────────────────────────────────────────────────

// Shared menu typography so every group renders the same way: a top-level
// section header (uppercase, muted) and, under review, a per-flag sub-label.
const SECTION_LABEL_CLASS =
  'px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground';
// Flag label + its Confirm/Dismiss share the same left indent so each flag reads
// as a self-contained subsection (label heads the column, actions sit under it).
const FLAG_LABEL_CLASS = 'px-2 py-1 pl-4 text-xs font-medium normal-case text-foreground';
const FLAG_ITEM_CLASS = 'pl-4';

interface QcReviewActionsMenuProps {
  evaluation: Evaluation;
  className?: string;
  /** Opens the "Add to Langfuse dataset" dialog (state owned by the page). */
  onAddToDataset: () => void;
  /** Whether the dataset action is available (needs a call id on the evaluation). */
  canAddToDataset: boolean;
}

export const QcReviewActionsMenu: React.FC<QcReviewActionsMenuProps> = ({
  evaluation,
  className,
  onAddToDataset,
  canAddToDataset,
}) => {
  const alerts = useMemo(() => getVisibleQcReviewAlerts(evaluation), [evaluation]);
  const unreviewedAlerts = useMemo(
    () => alerts.filter(alert => alert.review?.accepted === undefined),
    [alerts],
  );
  const [pendingDecision, setPendingDecision] = useState<PendingReviewDecision | null>(null);
  const [pendingAcceptAlert, setPendingAcceptAlert] = useState<QcReviewAlert | null>(null);
  const updateMutation = useUpdateEvaluation();
  // Confirming/dismissing an AI assessment writes back via UpdateEvaluation, so
  // these actions require evaluation:write for the current role. QC Reviewer is
  // read-only on evaluations and must not see the confirm/dismiss controls.
  const { hasPermission: canEditEvaluation } = useAPIPermission(
    API_METHODS.EvaluationService.UpdateEvaluation,
  );

  const orgId = evaluation.organization_id;
  const evalId = evaluation.id;
  const isLastUnreviewed = useMemo(() => countUnreviewedFlags(evaluation) === 1, [evaluation]);

  // Keep actions disabled while post-mutation refetch is in-flight to prevent
  // stale isLastUnreviewed from being used by a second rapid click.
  const isFetchingEval = useIsFetching({ queryKey: queryKeys.evaluations.byId(evalId ?? '') }) > 0;
  const isBusy = updateMutation.isPending || isFetchingEval;

  const handleAcceptDecision = useCallback(() => {
    if (!pendingAcceptAlert) return;

    const alert = pendingAcceptAlert;
    const newValue = alert.currentValue === true;
    const payload = buildReviewPayload(alert, orgId, evalId, isLastUnreviewed, undefined, newValue);

    updateMutation.mutate(payload, {
      onSuccess: () => {
        toast.success(`${alert.title} confirmed.`);
        setPendingAcceptAlert(null);
      },
    });
  }, [pendingAcceptAlert, orgId, evalId, isLastUnreviewed, updateMutation]);

  const handleDecisionSubmit = useCallback(
    (reason: string) => {
      if (!pendingDecision) return;

      const alert = pendingDecision.alert;
      const newValue = false;
      const payload = buildReviewPayload(alert, orgId, evalId, isLastUnreviewed, reason, newValue);

      updateMutation.mutate(payload, {
        onSuccess: () => {
          toast.success(`${alert.title} dismissed.`);
          setPendingDecision(null);
        },
      });
    },
    [pendingDecision, orgId, evalId, isLastUnreviewed, updateMutation],
  );

  const hasReviewActions = canEditEvaluation && unreviewedAlerts.length > 0;

  // Nothing to offer (not a 100ms user → no dataset action, and no flags to
  // review) → don't render the ⋯ menu at all.
  if (!canAddToDataset && !hasReviewActions) {
    return null;
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn('h-8 w-8 text-muted-foreground hover:bg-muted', className)}
            disabled={isBusy}
            aria-label="Open evaluation actions"
          >
            {isBusy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <MoreHorizontal className="h-4 w-4" />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          {canAddToDataset && (
            <>
              <DropdownMenuLabel className={SECTION_LABEL_CLASS}>Actions</DropdownMenuLabel>
              <DropdownMenuItem aria-label="Add to Langfuse dataset" onSelect={onAddToDataset}>
                <Database className="h-4 w-4 text-muted-foreground" />
                Add to Langfuse dataset
              </DropdownMenuItem>
            </>
          )}

          {hasReviewActions && (
            <>
              {canAddToDataset && <DropdownMenuSeparator />}
              <DropdownMenuLabel className={SECTION_LABEL_CLASS}>
                Review AI Assessment
              </DropdownMenuLabel>
              {unreviewedAlerts.map((alert, index) => (
                <React.Fragment key={alert.type}>
                  {index > 0 && <DropdownMenuSeparator />}
                  <DropdownMenuLabel className={FLAG_LABEL_CLASS}>
                    Review {alert.title}
                  </DropdownMenuLabel>
                  <DropdownMenuItem
                    className={FLAG_ITEM_CLASS}
                    aria-label={`Confirm ${alert.title}`}
                    disabled={isBusy}
                    onSelect={() => setPendingAcceptAlert(alert)}
                  >
                    <Check className="h-4 w-4 text-muted-foreground" strokeWidth={2.5} />
                    Confirm
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className={FLAG_ITEM_CLASS}
                    aria-label={`Dismiss ${alert.title}`}
                    disabled={isBusy}
                    onSelect={() => setPendingDecision({ alert })}
                  >
                    <X className="h-4 w-4 text-muted-foreground" strokeWidth={2.5} />
                    Dismiss
                  </DropdownMenuItem>
                </React.Fragment>
              ))}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <ReviewDecisionDialog
        decision={pendingDecision}
        isLoading={isBusy}
        onSubmit={handleDecisionSubmit}
        onCancel={() => setPendingDecision(null)}
      />
      <AcceptConfirmationDialog
        alert={pendingAcceptAlert}
        isLoading={isBusy}
        onConfirm={handleAcceptDecision}
        onCancel={() => setPendingAcceptAlert(null)}
      />
    </>
  );
};

// ─── Main component ────────────────────────────────────────────────────────────

interface QcReviewAlertsProps {
  evaluation: Evaluation;
  className?: string;
}

export const QcReviewAlerts: React.FC<QcReviewAlertsProps> = ({ evaluation, className }) => {
  const alerts = useMemo(() => getVisibleQcReviewAlerts(evaluation), [evaluation]);

  if (alerts.length === 0) {
    return null;
  }

  return (
    <div className={cn('space-y-3 border-t px-4 py-4', className)}>
      {alerts.map(alert => (
        <AlertBanner key={alert.type} alert={alert} />
      ))}
    </div>
  );
};
